// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Tool Palette
 *
 * Component catalog for the layout and pipeline builders. Items drag onto the
 * canvas (pointer or touch) and, when onNodeSelect is provided, tap to add.
 */

import { useState, useMemo, useEffect, useCallback, useDeferredValue } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import { useDraggable } from '@dnd-kit/core'
import { CSS } from '@dnd-kit/utilities'
import { ChevronRight, Search, Layers, Plus } from 'lucide-react'
import * as Icons from 'lucide-react'
import {
  InputWithIcons,
  Button,
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
  Tooltip,
  DarkTooltipContent,
  TooltipTrigger,
  TooltipProvider,
  cn,
} from '@/components/ui'
import { getAllPipelineNodes } from '../../../core/pipeline-registry'
import { getAllLayoutComponents } from '../../../core/layout-registry'
import type { PaletteItem, PaletteViewMode, ToolPaletteProps } from '../types'
import { buildCategories, filterCategoriesBySearch } from '../utils'

export const ToolPalette: React.FC<ToolPaletteProps> = ({
  onNodeSelect,
  viewMode,
  onViewModeChange,
  showModeToggle = false
}) => {
  const [searchTerm, setSearchTerm] = useState('')
  const deferredSearch = useDeferredValue(searchTerm)

  // Get items from the appropriate registry based on view mode
  const paletteItems = useMemo((): PaletteItem[] => {
    return viewMode === 'pipeline' ? getAllPipelineNodes() : getAllLayoutComponents()
  }, [viewMode])

  const categories = useMemo(() => buildCategories(paletteItems), [paletteItems])

  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    () => new Set(categories.map((category) => category.type))
  )

  useEffect(() => {
    setExpandedCategories(new Set(categories.map((category) => category.type)))
  }, [categories])

  useEffect(() => {
    setSearchTerm('')
  }, [viewMode])

  const filteredCategories = useMemo(
    () => filterCategoriesBySearch(categories, deferredSearch),
    [categories, deferredSearch]
  )

  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev)
      if (next.has(category)) next.delete(category)
      else next.add(category)
      return next
    })
  }

  const handleNativeDragStart = useCallback((event: React.DragEvent, item: PaletteItem) => {
    event.dataTransfer.setData('application/reactflow', item.id)
    event.dataTransfer.effectAllowed = 'move'
  }, [])

  // Get icon component
  const getIcon = (iconName?: string, className = 'h-4 w-4') => {
    if (!iconName) return null
    const IconComponent = (Icons as unknown as Record<string, React.ComponentType<{ className?: string }>>)[
      iconName
    ]
    return IconComponent ? <IconComponent className={className} /> : null
  }

  return (
    <TooltipProvider delayDuration={120}>
      <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-[#eef2f6] px-4 pb-3.5 pt-4">
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Layers className="h-4 w-4 text-slate-500" />
            <h2 className="text-sm font-semibold tracking-[-0.01em] text-slate-900">Components</h2>
          </div>
          {showModeToggle && onViewModeChange && (
            <div className="inline-flex items-center gap-0.5 rounded-md border border-[#e7ecf2] bg-slate-50 p-0.5">
              <Button
                size="sm"
                variant={viewMode === 'layout' ? 'default' : 'ghost'}
                className="h-6.5 px-2 text-xs"
                onClick={() => onViewModeChange('layout')}
              >
                Layout
              </Button>
              <Button
                size="sm"
                variant={viewMode === 'pipeline' ? 'default' : 'ghost'}
                className="h-6.5 px-2 text-xs"
                onClick={() => onViewModeChange('pipeline')}
              >
                Pipeline
              </Button>
            </div>
          )}
        </div>

        {/* Search */}
        <InputWithIcons
          type="text"
          placeholder="Search components"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          startIcon={Search}
          data-testid="builder-palette-search"
          endIcon={
            <kbd className="inline-flex h-5 select-none items-center rounded border border-[#e7ecf2] bg-slate-50 px-1.5 font-mono text-[10px] font-medium text-slate-400">
              /
            </kbd>
          }
          className="h-8.5 border-[#e7ecf2] bg-slate-50/60 text-[13px] placeholder:text-slate-400 focus-visible:ring-1 focus-visible:ring-ring/20"
        />
      </div>

      {/* Tool Categories */}
      <div className="flex-1 overflow-y-auto">
        <div className="px-4 pb-1.5 pt-3.5">
          <h3 className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-400">
            {viewMode === 'pipeline' ? 'Pipeline Nodes' : 'Layout Nodes'}
          </h3>
        </div>

        <div className="px-2.5 pb-4">
          {filteredCategories.map((category, categoryIndex) => {
            const items = category.items
            const isExpanded = expandedCategories.has(category.type)

            return (
              <div key={category.type} className={categoryIndex > 0 ? 'mt-1' : ''}>
                <Collapsible
                  open={isExpanded}
                  onOpenChange={() => toggleCategory(category.type)}
                >
                  <CollapsibleTrigger asChild>
                    <Button
                      variant="ghost"
                      className="group h-8.5 w-full justify-start px-2 font-normal hover:bg-slate-50"
                    >
                      <ChevronRight
                        className={cn(
                          'mr-1.5 h-3.5 w-3.5 shrink-0 text-slate-400 transition-transform duration-200',
                          isExpanded && 'rotate-90',
                        )}
                      />
                      {getIcon(category.icon, 'mr-2 h-4 w-4 shrink-0 text-slate-500')}
                      <span className="flex-1 text-left text-[13px] font-[550] text-slate-700">{category.label}</span>
                      <span className="text-[11px] tabular-nums text-slate-300 transition-colors group-hover:text-slate-400">
                        {items.length}
                      </span>
                    </Button>
                  </CollapsibleTrigger>

                  <CollapsibleContent>
                    <div className="ml-[1.15rem] mt-1 space-y-1 border-l border-slate-100 pb-2 pl-2.5 pr-1 animate-in fade-in duration-150">
                      {items.map((item) => (
                        <PaletteNodeItem
                          key={item.id}
                          item={item}
                          icon={getIcon(item.display.icon, 'h-4 w-4')}
                          viewMode={viewMode}
                          onSelect={onNodeSelect}
                          onNativeDragStart={handleNativeDragStart}
                        />
                      ))}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              </div>
            )
          })}
        </div>

        {/* No results message */}
        {searchTerm && filteredCategories.length === 0 && (
          <div className="flex flex-col items-center justify-center px-4 py-12 text-center animate-in fade-in duration-200">
            <div className="mb-2 flex h-9 w-9 items-center justify-center rounded-full bg-slate-100">
              <Search className="h-4 w-4 text-slate-400" />
            </div>
            <p className="text-[13px] font-[550] text-slate-700">No components found</p>
            <p className="mt-0.5 text-xs text-slate-400">Try adjusting your search</p>
          </div>
        )}
      </div>
      </div>
    </TooltipProvider>
  )
}

interface PaletteNodeItemProps {
  item: PaletteItem
  icon?: ReactNode
  viewMode: PaletteViewMode
  onSelect?: (item: PaletteItem) => void
  onNativeDragStart: (event: React.DragEvent, item: PaletteItem) => void
}

const PaletteNodeItem: React.FC<PaletteNodeItemProps> = ({
  item,
  icon,
  viewMode,
  onSelect,
  onNativeDragStart,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging,
  } = useDraggable({
    id: `palette-${item.id}`,
    data: {
      type: 'palette-node',
      nodeId: item.id,
    },
    disabled: viewMode !== 'layout',
  })

  const style: CSSProperties = {
    transform: viewMode === 'layout' && transform ? CSS.Translate.toString(transform) : undefined,
    opacity: viewMode === 'layout' && isDragging ? 0.5 : 1,
  }

  const iconNode = icon || <div className="h-4 w-4 rounded bg-slate-200" />
  const label = item.display.label
  const description = item.display.description

  const handleClick = () => {
    onSelect?.(item)
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {/* touch-none lets dnd-kit's touch sensor own the gesture on mobile
            instead of the browser scrolling the palette mid-drag */}
        <button
          ref={setNodeRef}
          type="button"
          data-testid={`builder-palette-node-${item.id}`}
          data-node-definition={item.id}
          style={style}
          className={cn(
            'group relative flex w-full items-center gap-2.5 rounded-lg border border-[#e7ecf2] bg-white px-2.5 py-2',
            'transition-all duration-150 hover:border-slate-300 hover:shadow-[0_1px_3px_rgba(2,8,23,0.06)]',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1',
            viewMode === 'layout' ? 'touch-none cursor-grab active:cursor-grabbing' : 'cursor-grab',
          )}
          onClick={handleClick}
          draggable={viewMode === 'pipeline'}
          onDragStart={viewMode === 'pipeline' ? (event) => onNativeDragStart(event, item) : undefined}
          {...(viewMode === 'layout' ? listeners : undefined)}
          {...(viewMode === 'layout' ? attributes : undefined)}
        >
          <span className="sr-only">{label}</span>
          <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md bg-slate-50 text-slate-500 transition-colors duration-150 group-hover:bg-slate-100 group-hover:text-slate-600">
            {iconNode}
          </span>
          <span className="min-w-0 flex-1 truncate text-left text-[13px] font-[550] text-slate-700">
            {label}
          </span>
          {onSelect && (
            <Plus className="h-3.5 w-3.5 flex-shrink-0 text-slate-300 opacity-0 transition-opacity duration-150 group-hover:opacity-100" />
          )}
        </button>
      </TooltipTrigger>
      <DarkTooltipContent side="right" align="start" className="max-w-xs">
        <p className="text-sm font-medium">{label}</p>
        {description && (
          <p className="mt-1 text-xs leading-snug opacity-80">{description}</p>
        )}
      </DarkTooltipContent>
    </Tooltip>
  )
}
